import { Pencil } from "lucide-react";
import { Mascot } from "@/components/brand/Mascot";
import type {
  TreasurePrefs,
  TreasureRewardId,
  TreasureSlot,
} from "@/lib/schemas";
import { cn } from "@/lib/utils/cn";
import { treasureRewardById } from "@/lib/utils/treasure";
import { treasureAssetPath, treasureVariantClass } from "./TreasureRewardIcon";

const SLOT_LABEL: Record<TreasureSlot, string> = {
  companion: "towarzysz",
  backpack: "plecak",
  glasses: "okulary",
  hoodie: "bluza",
  necklace: "łańcuch",
  hair: "fryzura",
  aura: "aura",
  scene: "tło",
};

const BACKPACK_PLACEMENT: Partial<Record<TreasureRewardId, string>> = {
  "explorer-backpack": "right-[1%] top-[48%] h-[32%] w-[38%]",
  "school-backpack": "right-[0] top-[45%] h-[37%] w-[42%]",
  "weekend-backpack": "right-[-1%] top-[42%] h-[42%] w-[46%]",
  "hiking-backpack": "right-[-2%] top-[37%] h-[48%] w-[50%]",
  "travel-backpack": "right-[-4%] top-[32%] h-[55%] w-[56%]",
};

const GLASSES_PLACEMENT: Partial<Record<TreasureRewardId, string>> = {
  "round-glasses": "left-[36.5%] top-[23.5%] w-[39%]",
  "dark-sunglasses": "left-[36.5%] top-[23.7%] w-[39%]",
  "red-sunglasses": "left-[36.5%] top-[24.1%] w-[39%]",
  "mirror-glasses": "left-[34%] top-[23%] w-[44%]",
  "cat-eye-glasses": "left-[34.5%] top-[24%] w-[43%]",
  "dollar-glasses": "left-[36%] top-[21%] w-[40%]",
};

const HAIR_PLACEMENT: Partial<Record<TreasureRewardId, string>> = {
  "blue-mohawk": "left-[16%] top-[-15%] w-[87.285%]",
  "berry-mohawk": "left-[15%] top-[-16%] w-[91.08%]",
  "sunny-mohawk": "left-[17%] top-[-15%] w-[85.3875%]",
};

const NECKLACE_PLACEMENT: Partial<Record<TreasureRewardId, string>> = {
  "silver-star-chain": "left-[35%] top-[51%] w-[31%]",
  "gold-dollar-chain": "left-[34%] top-[50%] w-[33%]",
  "clover-chain": "left-[35%] top-[50%] w-[31%]",
};

const COMPANION_PLACEMENT: Partial<Record<TreasureRewardId, string>> = {
  "fox-companion": "-bottom-[1%] -left-[25%] w-[40%]",
  "cat-companion": "bottom-[0] -left-[21%] w-[35%]",
  "dog-companion": "-bottom-[1%] -left-[23%] w-[38%]",
  "chicken-companion": "bottom-[0] -left-[19%] w-[33%]",
  "tiger-companion": "-bottom-[1%] -left-[23%] w-[39%]",
};

export function HeroAvatar({
  treasure,
  size = 170,
  onClick,
  className,
}: {
  treasure: TreasurePrefs;
  size?: number;
  onClick?: () => void;
  className?: string;
}) {
  const equipped = treasure.equipped ?? {};
  const companion = treasureRewardById(equipped.companion);
  const backpack = treasureRewardById(equipped.backpack);
  const glasses = treasureRewardById(equipped.glasses);
  const hoodie = treasureRewardById(equipped.hoodie);
  const necklace = treasureRewardById(equipped.necklace);
  const hair = treasureRewardById(equipped.hair);
  const aura = treasureRewardById(equipped.aura);
  const equippedLabels = Object.entries(equipped)
    .map(([slot]) => SLOT_LABEL[slot as TreasureSlot])
    .join(", ");

  const content = (
    <>
      {aura && (
        <span
          className={cn(
            "treasure-avatar-aura pointer-events-none absolute -inset-[12%] z-0",
            `treasure-avatar-aura-${aura.variant}`,
          )}
          aria-hidden
        >
          <span className="absolute left-[2%] top-[18%]">✦</span>
          <span className="absolute right-[-2%] top-[36%]">✧</span>
          <span className="absolute bottom-[8%] left-[5%]">✦</span>
        </span>
      )}

      {backpack && (
        <img
          src={treasureAssetPath(backpack)}
          alt=""
          className={cn(
            "pointer-events-none absolute z-0 rotate-[8deg] object-contain",
            BACKPACK_PLACEMENT[backpack.id],
            treasureVariantClass(backpack),
          )}
          draggable={false}
        />
      )}

      <Mascot pose="idle" size={size} className="relative z-[1]" />

      {hoodie && (
        <span
          className={cn(
            "treasure-hoodie-tint pointer-events-none absolute inset-0 z-[2]",
            `treasure-hoodie-${hoodie.variant}`,
          )}
          aria-hidden
        />
      )}

      {hair && (
        <img
          src={treasureAssetPath(hair)}
          alt=""
          className={cn(
            "pointer-events-none absolute z-[3] h-auto object-contain drop-shadow-[0_4px_4px_rgba(34,52,40,0.14)]",
            HAIR_PLACEMENT[hair.id],
            treasureVariantClass(hair),
          )}
          draggable={false}
        />
      )}

      {glasses && (
        <img
          src={treasureAssetPath(glasses)}
          alt=""
          className={cn(
            "pointer-events-none absolute z-[3] h-auto object-contain",
            GLASSES_PLACEMENT[glasses.id],
            treasureVariantClass(glasses),
          )}
          draggable={false}
        />
      )}

      {necklace && (
        <img
          src="/treasure/accessories/chain.webp?v=1"
          alt=""
          className={cn(
            "pointer-events-none absolute z-[4] h-auto object-contain drop-shadow-[0_3px_3px_rgba(34,52,40,0.18)]",
            NECKLACE_PLACEMENT[necklace.id],
            treasureVariantClass(necklace),
          )}
          draggable={false}
        />
      )}

      {companion && (
        <img
          src={treasureAssetPath(companion)}
          alt=""
          className={cn(
            "pointer-events-none absolute z-[3] h-auto object-contain drop-shadow-[0_5px_6px_rgba(34,52,40,0.2)]",
            COMPANION_PLACEMENT[companion.id],
          )}
          draggable={false}
        />
      )}

      {onClick && (
        <span className="absolute bottom-[7%] right-[-4%] z-[5] grid h-8 w-8 place-items-center rounded-full border-2 border-white bg-green text-white shadow-lg">
          <Pencil className="h-3.5 w-3.5" />
        </span>
      )}
    </>
  );

  const avatarStyle = { height: size, width: Math.round(size * 0.75) };
  if (!onClick) {
    return (
      <div
        className={cn("relative", className)}
        style={avatarStyle}
        aria-hidden
      >
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "relative rounded-[28px] transition-transform hover:scale-[1.02] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-sunny/70",
        className,
      )}
      style={avatarStyle}
      aria-label={`Otwórz garderobę bohatera${equippedLabels ? `. Założone: ${equippedLabels}` : ""}`}
    >
      {content}
    </button>
  );
}
